/**
 * +world-resources: the toon renderer's world-loot layer (docs/design/world-resources.md).
 *  - breakables / hidden caches / reagent nodes: one merged mesh + outline + shadow (objects.ts),
 *    hit flash, squash and wobble, break pop, remains, a gold rim + twinkle when the looter is near
 *  - hidden caches: a cracked mound with a jade glint until discovered, then the chest rises
 *    with a jade burst; standing at it fills a progress ring
 *  - break fx: pooled physics shards, dust puff, flash ring, small screen shake (local looter only)
 *  - pickups: arc + bounce + hover with a rarity glow / beam, magnet zip with a trail, pop
 * Draw calls: objects 1 (+1 hull, +1 shadow), shards 1, pickups 1, glows 1, sparks 1, dust 1.
 * Steady state allocates nothing per frame (pools, preallocated sprite / transient buffers).
 */
import * as THREE from 'three';
import type { DungeonDef, PickupRarity, SimEvent, UnitId, Vec2, WorldObjectDef, WorldObjectKind, WorldResourceKind } from '@keystone/sim';
import type { RenderFrame, RenderUnit } from '../../types';
import type { ToonAssets } from '../assets';
import { groundY, objY } from '../ground';
import type { HullRegistry } from '../style';
import { ParticleBatch } from '../fx/particles';
import { PF_BOUNCE, PF_FLOOR_FADE, ParticlePool, blankParticle } from '../fx/pool';
import { FRAME, createFxAtlas } from '../fx/textures';
import { CHUNK_COLORS, DUST_COLOR, JADE_GLINT, NODE_GLOW, RARITY_GLOW, RARITY_GLOW_K, type RGB3 } from '../fx/loot/palette';
import { type PickupFxSink, PickupsView } from '../fx/loot/pickups';
import { ShardPool, blankShard } from '../fx/loot/shards';
import { ModelLibrary, kindHeight } from './models';
import { type ObjectState, WorldObjectsMesh } from './objects';
import { GlowSprites, SHAPE } from './sprites';

/** Near cue: breakables within this range of the looter get a gold rim (yd). */
const NEAR_RANGE = 6;
const BREAK_POP_MS = 70;
const REVEAL_MS = 480;
const TRANSIENTS = 64;

/** Shake per event for the local looter (CameraShake trauma). */
const SHAKE = { hit: 0.1, breakable: 0.2, node: 0.28, cache: 0.24 } as const;
/** Shards per break by kind. */
const SHARDS: Readonly<Record<WorldObjectKind, number>> = { urn: 13, crate: 11, bone_pile: 9, egg_clutch: 11, cocoon: 9, offering_bowl: 9, hidden_cache: 12, treasure_chest: 16, venom_gland: 14, eye_shard: 13 };

interface Transient {
  live: boolean;
  shape: number;
  x: number;
  y: number;
  z: number;
  t0: number;
  dur: number;
  s0: number;
  s1: number;
  h: number;
  param: number;
  r: number;
  g: number;
  b: number;
  a: number;
}

const srgbToLinear = (hex: number, out: THREE.Color): THREE.Color => out.setHex(hex);
const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const easeOutBack = (t: number): number => {
  const c = 1.9;
  const u = t - 1;
  return 1 + (c + 1) * u * u * u + c * u * u;
};

export class WorldLootView {
  readonly group = new THREE.Group();
  private lib: ModelLibrary | null = null;
  private objects: WorldObjectsMesh | null = null;
  private defs: readonly WorldObjectDef[] = [];
  private readonly index = new Map<string, number>();
  private revealAt = new Float64Array(0);
  private openProg = new Float32Array(0);
  private readonly sprites = new GlowSprites(384);
  private readonly shards = new ShardPool(220);
  private readonly pickups = new PickupsView(128);
  /** +field: objects come and go at runtime (field chests): see syncDynamic. */
  private dynamic = false;
  private readonly sparkPool = new ParticlePool(256);
  private readonly dustPool = new ParticlePool(128);
  private readonly atlas: THREE.Texture | null;
  private readonly sparks: ParticleBatch | null;
  private readonly dust: ParticleBatch | null;
  private readonly uFx = { value: 1 };
  private readonly transients: Transient[];
  private readonly st: ObjectState = { intact: 1, flash: 0, tiltX: 0, tiltZ: 0, remains: 0, hint: 0, near: 0, rise: 0 };
  private readonly pi = blankParticle();
  private readonly si = blankShard();
  private readonly col = new THREE.Color();
  private readonly looterPos = { x: 0, y: 0, h: 0 }; // +glide: h = height over the ground (on a platform, gliding)
  private hasLooter = false;
  private nowMs = 0;
  private playerIsLooter = false;
  private looterIdCache: UnitId | null = null;
  /** Last frame's world-loot state (dev inspection). */
  lastWorld: RenderFrame['world'] | null = null;
  private readonly sink: PickupFxSink;

  constructor(
    private readonly assets: ToonAssets,
    private readonly hulls: HullRegistry | null,
    private readonly shake: (amount: number) => void,
  ) {
    this.group.name = 'worldLoot';
    // Dev-only console handle (stats, last world-loot frame).
    if (import.meta.env?.DEV) Object.assign(globalThis as object, { __worldLoot: this });
    this.atlas = typeof document !== 'undefined' ? createFxAtlas() : null;
    this.sparks = this.atlas ? new ParticleBatch(this.sparkPool, this.atlas, this.uFx, true, 9) : null;
    this.dust = this.atlas ? new ParticleBatch(this.dustPool, this.atlas, this.uFx, false, 7) : null;
    this.group.add(this.shards.mesh, this.pickups.mesh, this.sprites.mesh);
    if (this.sparks) this.group.add(this.sparks.mesh);
    if (this.dust) this.group.add(this.dust.mesh);
    this.transients = Array.from({ length: TRANSIENTS }, () => ({ live: false, shape: 0, x: 0, y: 0, z: 0, t0: 0, dur: 1, s0: 0, s1: 0, h: 0, param: 0, r: 0, g: 0, b: 0, a: 0 }));
    this.sink = {
      glow: (x, y, z, gy, rarity, k, beam, air, phase) => this.pickupGlow(x, y, z, gy, rarity, k, beam, air, phase),
      trail: (x, y, z, vx, vy, vz, rarity) => this.pickupTrail(x, y, z, vx, vy, vz, rarity),
      pop: (x, y, z, rarity, resource) => this.pickupPop(x, y, z, rarity, resource),
      land: (x, y, z, rarity) => this.pickupLand(x, y, z, rarity),
    };
  }

  /** Build the static object mesh for a dungeon (call after setGroundForDungeon). */
  init(d: DungeonDef | undefined): void {
    this.clear();
    this.dynamic = !!d?.field;
    this.defs = d?.worldObjects ?? [];
    this.build();
  }

  /** (Re)build the object mesh for `this.defs`. */
  private build(): void {
    this.objects?.dispose();
    this.objects = null;
    this.index.clear();
    if (!this.defs.length) return;
    this.lib ??= new ModelLibrary({ kit: (id) => this.assets.kitGeometry(id) });
    this.objects = new WorldObjectsMesh(this.defs, this.lib, this.assets.propAtlas(), this.hulls);
    this.group.add(this.objects.group);
    this.defs.forEach((o, i) => this.index.set(o.id, i));
    this.revealAt = new Float64Array(this.defs.length).fill(-1);
    this.openProg = new Float32Array(this.defs.length);
  }

  private clear(): void {
    this.objects?.dispose();
    this.objects = null;
    this.lib?.dispose();
    this.lib = null;
    this.defs = [];
    this.index.clear();
    this.shards.clear();
    this.pickups.clear();
    this.sparkPool.clear();
    this.dustPool.clear();
    for (const t of this.transients) t.live = false;
  }

  /**
   * +field: field maps spawn objects at runtime (treasure chests) and drop them again: rebuild the
   * (tiny) object mesh whenever the live id list changes. Static dungeons never take this path.
   */
  private syncDynamic(w: NonNullable<RenderFrame['world']>): void {
    const live = w.objects;
    let same = live.length === this.defs.length;
    for (let i = 0; same && i < live.length; i++) if (live[i]!.id !== this.defs[i]!.id) same = false;
    if (same) return;
    this.defs = live.map((o) => ({ id: o.id, kind: o.kind, class: o.class, pos: { x: o.pos.x, y: o.pos.y }, facing: o.facing, roomId: o.roomId, hp: o.maxHp, radius: o.radius, loot: [], zone: 'off' as const, ...(o.z !== undefined ? { z: o.z } : {}) })); // +glide: a sky chest stands on its platform
    this.build();
  }

  /** Per frame (after units): events → fx, object states, pickups, glows. `dt` = fx seconds. */
  update(frame: RenderFrame, dt: number, units: ReadonlyMap<UnitId, RenderUnit>, fxIntensity: number): void {
    const w = frame.world;
    if (this.dynamic && w) this.syncDynamic(w);
    this.lastWorld = w ?? null;
    this.uFx.value = fxIntensity;
    this.sprites.uFx.value = Math.min(1.2, 0.5 + 0.5 * fxIntensity);
    this.nowMs = frame.timeMs;
    const looter = w?.looterId ? units.get(w.looterId) : undefined;
    this.hasLooter = !!looter && looter.alive;
    if (looter) {
      this.looterPos.x = looter.pos.x;
      this.looterPos.y = looter.pos.y;
      this.looterPos.h = looter.jumpH ?? 0;
    }
    this.playerIsLooter = !!w?.looterId && w.looterId === frame.playerId;
    this.looterIdCache = w?.looterId ?? null;
    this.sprites.begin();
    if (frame.events) for (const e of frame.events) this.ingest(e);
    // No world-loot state (worldObjects disabled for this run): the props are not in play.
    if (this.objects) this.objects.group.visible = !!w;
    if (w && this.objects) this.updateObjects(frame, dt);
    this.pickups.update(w?.pickups ?? [], frame.timeMs, dt, this.hasLooter ? this.looterPos : null, this.sink);
    this.updateTransients();
    this.shards.update(dt);
    this.sparkPool.update(dt);
    this.dustPool.update(dt);
    this.sparks?.sync();
    this.dust?.sync();
    this.sprites.end();
    this.objects?.flush();
  }

  private ingest(e: SimEvent): void {
    switch (e.type) {
      case 'object_hit': {
        const i = this.index.get(e.objectId);
        const d = i === undefined ? undefined : this.defs[i];
        if (!d) return;
        this.burstShards(d, 3, 0.55);
        this.puff(d, 2, 0.6);
        if (this.playerIsLooter && e.sourceID === this.looterId()) this.shake(SHAKE.hit);
        return;
      }
      case 'object_break': {
        const i = this.index.get(e.objectId);
        const d = i === undefined ? undefined : this.defs[i];
        if (!d) return;
        this.breakFx(d, e.drops);
        if (this.playerIsLooter && e.sourceID === this.looterId()) this.shake(e.objectClass === 'node' ? SHAKE.node : e.objectClass === 'cache' ? SHAKE.cache : SHAKE.breakable);
        return;
      }
      case 'object_discovered': {
        const i = this.index.get(e.objectId);
        if (i === undefined) return;
        this.revealAt[i] = this.nowMs;
        const gy = groundY(e.pos.x, e.pos.y);
        const [r, g, b] = JADE_GLINT;
        this.transient(SHAPE.RING, e.pos.x, gy + 0.05, -e.pos.y, 520, 0.3, 2.6, 0, 0.18, r, g, b, 0.7);
        this.transient(SHAPE.BEAM, e.pos.x, gy, -e.pos.y, 900, 0.5, 0.5, 3.2, 0, r, g, b, 0.45);
        this.sparkBurst(e.pos.x, gy + 0.5, -e.pos.y, 10, JADE_GLINT, 3.2, 0.7);
        return;
      }
      case 'pickup_spawn': {
        const gy = groundY(e.from.x, e.from.y);
        const c = RARITY_GLOW[e.rarity];
        this.sparkBurst(e.from.x, gy + 0.7, -e.from.y, 2, c, 2.2, 0.45);
        return;
      }
      case 'pickup':
        this.pickups.collect(e.pickupId);
        return;
      default:
        return;
    }
  }

  private looterId(): UnitId | null {
    return this.looterIdCache;
  }

  private updateObjects(frame: RenderFrame, dt: number): void {
    const w = frame.world!;
    const t = frame.timeMs;
    const tS = t / 1000;
    const st = this.st;
    const lx = this.looterPos.x;
    const ly = this.looterPos.y;
    for (const o of w.objects) {
      const i = this.index.get(o.id);
      if (i === undefined) continue;
      const def = this.defs[i]!;
      st.intact = 1;
      st.flash = 0;
      st.tiltX = 0;
      st.tiltZ = 0;
      st.remains = 0;
      st.hint = 0;
      st.near = 0;
      st.rise = 0;
      const gx = o.pos.x;
      const gz = -o.pos.y;
      const gy = objY(o);
      const h = kindHeight(o.kind);
      if (o.broken) {
        const db = o.brokenAt === null ? 1e9 : t - o.brokenAt;
        if (db < BREAK_POP_MS && db >= 0) {
          st.intact = 1 + 0.22 * (db / BREAK_POP_MS);
          st.flash = 1;
        } else st.intact = 0;
        const r = smooth(30, 260, db);
        st.remains = r < 1 ? easeOutBack(r) : 1;
        this.openProg[i] = 0;
      } else if (o.hidden && !o.discovered) {
        st.intact = 0;
        st.hint = 1;
        // Hint: a jade glint every few seconds and a barely-there jade pool. Rewards a curious eye.
        const ph = (tS + i * 0.37) % 3.4;
        const [r, g, b] = JADE_GLINT;
        this.sprites.push(SHAPE.DISC, gx, gy + 0.04, gz, 0.9, 0, 0, 0, r, g, b, 0.07 + 0.03 * Math.sin(tS * 1.3 + i));
        if (ph < 0.6) this.sprites.push(SHAPE.STAR, gx + 0.12, gy + 0.45, gz - 0.18, 0.55, 0.55, 0, ph * 1.2, r, g, b, 0.75 * Math.sin((ph / 0.6) * Math.PI));
      } else {
        // Hidden cache just found: the chest rises out of the mound.
        const ra = this.revealAt[i]!;
        if (o.hidden && ra >= 0 && t - ra < REVEAL_MS) {
          const k = Math.max(0, (t - ra) / REVEAL_MS);
          st.intact = easeOutBack(k);
          st.hint = 1 - smooth(0, 0.6, k);
          st.rise = -0.5 * (1 - k);
        }
        if (o.lastHitAt !== null) {
          const dh = t - o.lastHitAt;
          if (dh >= 0 && dh < 900) {
            st.flash = Math.max(0, 1 - dh / 130);
            const dS = dh / 1000;
            const wob = 0.34 * Math.exp(-dS * 7) * Math.sin(dS * 32);
            let dx = gx - lx;
            let dz = gz + ly;
            const l = Math.hypot(dx, dz) || 1;
            dx /= l;
            dz /= l;
            st.tiltX = dx * wob;
            st.tiltZ = dz * wob;
          }
        }
        if (o.class === 'cache') {
          // Opening: the lid rattles and a progress ring fills.
          const target = Math.min(1, o.openProgressMs / 1500);
          const p = this.openProg[i]!;
          const np = target <= 0 ? 0 : p + (target - p) * Math.min(1, dt * 10);
          this.openProg[i] = np;
          const [r, g, b] = NODE_GLOW.hidden_cache!;
          this.sprites.push(SHAPE.DISC, gx, gy + 0.04, gz, 1.3, 0, 0, 0, r, g, b, 0.22 + 0.06 * Math.sin(tS * 2.2));
          this.sprites.push(SHAPE.BEAM, gx, gy, gz, 0.9, 2.2, 0, 0, 1.0, 0.82, 0.42, 0.16);
          // +field: a treasure chest calls from across the field: a tall golden light beam + glints.
          if (o.kind === 'treasure_chest') {
            const pulse = 0.5 + 0.5 * Math.sin(tS * 2.4);
            this.sprites.push(SHAPE.BEAM, gx, gy, gz, 1.5, 22, 0, 0, 1.0, 0.8, 0.35, 0.34 + 0.1 * pulse);
            this.sprites.push(SHAPE.BEAM, gx, gy, gz, 0.6, 30, 0, 0, 1.0, 0.95, 0.75, 0.3);
            this.sprites.push(SHAPE.GLOW, gx, gy + 1.0, gz, 2.6, 2.6, 0, 0, 1.0, 0.82, 0.4, 0.35 + 0.15 * pulse);
            const ph = (tS * 0.9) % 1;
            this.sprites.push(SHAPE.STAR, gx + 0.4 * Math.sin(tS * 3), gy + 0.6 + ph * 2.4, gz + 0.4 * Math.cos(tS * 3), 0.5, 0.5, 0, tS, 1.0, 0.9, 0.6, 0.8 * (1 - ph));
          }
          if (np > 0.01) {
            this.sprites.push(SHAPE.ARC, gx, gy + 0.06, gz, 1.25, 0, np, 0, 1.0, 0.85, 0.45, 0.85);
            const rattle = 0.05 * np * Math.sin(tS * 38);
            st.tiltX += rattle;
            st.flash = Math.max(st.flash, 0.25 * np * (0.5 + 0.5 * Math.sin(tS * 18)));
          }
        } else {
          const nodeGlow = NODE_GLOW[o.kind];
          if (nodeGlow) {
            const [r, g, b] = nodeGlow;
            const pulse = 0.5 + 0.5 * Math.sin(tS * 2.4 + i);
            this.sprites.push(SHAPE.DISC, gx, gy + 0.04, gz, 1.2, 0, 0, 0, r, g, b, 0.16 + 0.08 * pulse);
            this.sprites.push(SHAPE.GLOW, gx, gy + h * 0.55, gz, 1.6, 1.6, 0, 0, r, g, b, 0.1 + 0.06 * pulse);
          }
          if (this.hasLooter) {
            const d = Math.hypot(o.pos.x - lx, o.pos.y - ly) - def.radius;
            if (d < NEAR_RANGE) {
              const k = smooth(NEAR_RANGE, 2.5, d);
              st.near = k * (0.7 + 0.3 * Math.sin(tS * 4 + i));
              // A small gold twinkle on top now and then: "this one breaks".
              const ph = (tS * 0.8 + i * 0.61) % 1.6;
              if (ph < 0.35) this.sprites.push(SHAPE.STAR, gx + 0.15 * Math.sin(i), gy + h * 0.95, gz + 0.1 * Math.cos(i), 0.45, 0.45, 0, i, 1.0, 0.86, 0.5, 0.55 * k * Math.sin((ph / 0.35) * Math.PI));
            }
          }
        }
      }
      this.objects!.set(i, st);
    }
  }

  // ---- break fx ------------------------------------------------------------------------

  private breakFx(d: WorldObjectDef, drops: number): void {
    const n = SHARDS[d.kind] ?? 10;
    const big = d.class !== 'breakable';
    this.burstShards(d, n, 1);
    this.puff(d, big ? 8 : 6, 1);
    const gy = objY(d);
    const h = kindHeight(d.kind);
    // Flash: a warm pop at the object's heart + a fast ground ring.
    this.transient(SHAPE.GLOW, d.pos.x, gy + h * 0.5, -d.pos.y, 220, 0.8, 2.2, 0, 0, 1.0, 0.86, 0.62, 0.55);
    this.transient(SHAPE.RING, d.pos.x, gy + 0.05, -d.pos.y, 320, 0.3, 1.7, 0, 0.22, 1.0, 0.85, 0.6, 0.5);
    const glow = NODE_GLOW[d.kind];
    if (glow) this.sparkBurst(d.pos.x, gy + h * 0.5, -d.pos.y, 14, glow, 4.5, 0.8);
    if (drops > 0) this.sparkBurst(d.pos.x, gy + h * 0.6, -d.pos.y, 4 + drops * 2, [1.0, 0.82, 0.42], 3.4, 0.6);
    if (d.kind === 'venom_gland') this.splash(d, [0.22, 0.75, 0.25], 10);
  }

  private burstShards(d: WorldObjectDef, n: number, power: number): void {
    const cols = CHUNK_COLORS[d.kind] ?? CHUNK_COLORS.urn;
    const gy = objY(d);
    const h = kindHeight(d.kind);
    const s = this.si;
    // Away from the looter (a kick), with a full spread.
    let bx = d.pos.x - this.looterPos.x;
    let bz = -(d.pos.y - this.looterPos.y);
    const bl = Math.hypot(bx, bz) || 1;
    bx /= bl;
    bz /= bl;
    for (let k = 0; k < n; k++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (1.6 + Math.random() * 3.4) * power;
      s.x = d.pos.x + Math.cos(a) * d.radius * 0.5;
      s.z = -d.pos.y + Math.sin(a) * d.radius * 0.5;
      s.y = gy + h * (0.25 + Math.random() * 0.6);
      s.vx = Math.cos(a) * sp + bx * 1.6 * power;
      s.vz = Math.sin(a) * sp + bz * 1.6 * power;
      s.vy = (3 + Math.random() * 4) * power;
      s.size = (0.13 + Math.random() * 0.17) * (power < 1 ? 0.7 : 1) * (d.class === 'breakable' ? 1 : 1.2);
      srgbToLinear(cols[k % cols.length]!, this.col);
      s.r = this.col.r;
      s.g = this.col.g;
      s.b = this.col.b;
      s.linger = power < 1 ? 0 : 0.6 + Math.random() * 0.8;
      this.shards.spawn(s);
    }
  }

  private puff(d: WorldObjectDef, n: number, power: number): void {
    const [r, g, b] = DUST_COLOR[d.kind] ?? DUST_COLOR.urn;
    const gy = objY(d);
    const p = this.pi;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + Math.random();
      const sp = (0.8 + Math.random() * 1.2) * power;
      p.x = d.pos.x + Math.cos(a) * 0.2;
      p.z = -d.pos.y + Math.sin(a) * 0.2;
      p.y = gy + 0.25 + Math.random() * 0.4;
      p.vx = Math.cos(a) * sp;
      p.vz = Math.sin(a) * sp;
      p.vy = 0.4 + Math.random() * 0.6;
      p.life = 0.55 + Math.random() * 0.4;
      p.size0 = 0.5 * power;
      p.size1 = (1.3 + Math.random() * 0.5) * power;
      p.rot = Math.random() * 6.28;
      p.rotVel = (Math.random() - 0.5) * 2;
      p.r0 = r;
      p.g0 = g;
      p.b0 = b;
      p.a0 = 0.5;
      p.r1 = r * 0.8;
      p.g1 = g * 0.8;
      p.b1 = b * 0.8;
      p.a1 = 0;
      p.gravity = -0.3;
      p.drag = 2.5;
      p.frame = FRAME.dust;
      p.stretch = 0;
      p.flags = 0;
      p.priority = 2;
      p.cost = 0.4;
      this.dustPool.spawn(p);
    }
  }

  private splash(d: WorldObjectDef, c: RGB3, n: number): void {
    const gy = objY(d);
    const p = this.pi;
    for (let k = 0; k < n; k++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 2 + Math.random() * 3;
      p.x = d.pos.x;
      p.z = -d.pos.y;
      p.y = gy + 0.5;
      p.vx = Math.cos(a) * sp;
      p.vz = Math.sin(a) * sp;
      p.vy = 2.5 + Math.random() * 3;
      p.life = 0.6 + Math.random() * 0.3;
      p.size0 = 0.2;
      p.size1 = 0.12;
      p.rot = 0;
      p.rotVel = 0;
      p.r0 = c[0];
      p.g0 = c[1];
      p.b0 = c[2];
      p.a0 = 0.85;
      p.r1 = c[0] * 0.6;
      p.g1 = c[1] * 0.6;
      p.b1 = c[2] * 0.6;
      p.a1 = 0;
      p.gravity = 14;
      p.drag = 0.5;
      p.frame = FRAME.core;
      p.stretch = 0.08;
      p.flags = PF_BOUNCE;
      p.priority = 2;
      p.cost = 0.2;
      this.dustPool.spawn(p);
    }
  }

  private sparkBurst(x: number, y: number, z: number, n: number, c: RGB3, speed: number, life: number): void {
    const p = this.pi;
    for (let k = 0; k < n; k++) {
      const a = Math.random() * Math.PI * 2;
      const up = Math.random();
      const sp = speed * (0.5 + Math.random() * 0.6);
      p.x = x;
      p.y = y;
      p.z = z;
      p.vx = Math.cos(a) * sp * (1 - up * 0.5);
      p.vz = Math.sin(a) * sp * (1 - up * 0.5);
      p.vy = 1 + up * sp;
      p.life = life * (0.7 + Math.random() * 0.5);
      p.size0 = 0.22;
      p.size1 = 0.05;
      p.rot = Math.random() * 6.28;
      p.rotVel = 4;
      p.r0 = c[0];
      p.g0 = c[1];
      p.b0 = c[2];
      p.a0 = 0.9;
      p.r1 = c[0];
      p.g1 = c[1];
      p.b1 = c[2];
      p.a1 = 0;
      p.gravity = 5;
      p.drag = 2;
      p.frame = FRAME.star;
      p.stretch = 0;
      p.flags = PF_FLOOR_FADE;
      p.priority = 3;
      p.cost = 0.15;
      this.sparkPool.spawn(p);
    }
  }

  // ---- pickups -------------------------------------------------------------------------

  private pickupGlow(x: number, y: number, z: number, gy: number, rarity: PickupRarity, k: number, beam: number, air: boolean, phase: number): void {
    const [r, g, b] = RARITY_GLOW[rarity];
    const gk = RARITY_GLOW_K[rarity] * k;
    const tS = this.nowMs / 1000;
    const pulse = 0.85 + 0.15 * Math.sin(tS * 3 + phase);
    this.sprites.push(SHAPE.GLOW, x, y, z, 0.95, 0.95, 0, 0, r, g, b, 0.42 * gk * pulse);
    if (air) return;
    this.sprites.push(SHAPE.DISC, x, gy + 0.035, z, 0.38 + 0.22 * RARITY_GLOW_K[rarity], 0, 0, 0, r, g, b, 0.38 * gk * pulse);
    if (beam > 0) this.sprites.push(SHAPE.BEAM, x, gy, z, 0.34, beam * (0.92 + 0.08 * pulse), 0, 0, r, g, b, 0.3 * gk);
    if (rarity === 'epic' || rarity === 'legendary') {
      const ph = (tS * 0.9 + phase) % 1.4;
      if (ph < 0.3) this.sprites.push(SHAPE.STAR, x + 0.12 * Math.sin(phase * 7), y + 0.2, z + 0.12 * Math.cos(phase * 5), 0.5, 0.5, 0, phase, r, g, b, 0.8 * Math.sin((ph / 0.3) * Math.PI));
    }
  }

  private pickupTrail(x: number, y: number, z: number, vx: number, vy: number, vz: number, rarity: PickupRarity): void {
    const [r, g, b] = RARITY_GLOW[rarity];
    const p = this.pi;
    p.x = x;
    p.y = y;
    p.z = z;
    p.vx = -vx * 0.08;
    p.vy = -vy * 0.08;
    p.vz = -vz * 0.08;
    p.life = 0.22;
    p.size0 = 0.2;
    p.size1 = 0.04;
    p.rot = 0;
    p.rotVel = 0;
    p.r0 = r;
    p.g0 = g;
    p.b0 = b;
    p.a0 = 0.75;
    p.r1 = r;
    p.g1 = g;
    p.b1 = b;
    p.a1 = 0;
    p.gravity = 0;
    p.drag = 0;
    p.frame = FRAME.glow;
    p.stretch = 0;
    p.flags = 0;
    p.priority = 3;
    p.cost = 0.05;
    this.sparkPool.spawn(p);
  }

  private pickupPop(x: number, y: number, z: number, rarity: PickupRarity, _resource: WorldResourceKind): void {
    const c = RARITY_GLOW[rarity];
    const big = rarity === 'epic' || rarity === 'legendary';
    this.transient(SHAPE.GLOW, x, y, z, 200, 0.4, big ? 1.8 : 1.2, 0, 0, c[0], c[1], c[2], 0.65);
    this.transient(SHAPE.RING, x, groundY(x, -z) + 0.05, z, 280, 0.2, big ? 1.4 : 0.9, 0, 0.25, c[0], c[1], c[2], 0.55);
    this.sparkBurst(x, y, z, big ? 10 : 5, c, 2.6, 0.45);
  }

  private pickupLand(x: number, y: number, z: number, rarity: PickupRarity): void {
    const c = RARITY_GLOW[rarity];
    this.transient(SHAPE.RING, x, y + 0.04, z, 260, 0.1, 0.55, 0, 0.3, c[0], c[1], c[2], 0.4 * RARITY_GLOW_K[rarity]);
  }

  // ---- transient sprites (timed rings / flashes / beams) --------------------------------

  private transient(shape: number, x: number, y: number, z: number, durMs: number, s0: number, s1: number, h: number, param: number, r: number, g: number, b: number, a: number): void {
    let slot: Transient | undefined;
    let oldest: Transient | undefined;
    for (const t of this.transients) {
      if (!t.live) {
        slot = t;
        break;
      }
      if (!oldest || t.t0 < oldest.t0) oldest = t;
    }
    slot ??= oldest!;
    slot.live = true;
    slot.shape = shape;
    slot.x = x;
    slot.y = y;
    slot.z = z;
    slot.t0 = this.nowMs;
    slot.dur = durMs;
    slot.s0 = s0;
    slot.s1 = s1;
    slot.h = h;
    slot.param = param;
    slot.r = r;
    slot.g = g;
    slot.b = b;
    slot.a = a;
  }

  private updateTransients(): void {
    for (const t of this.transients) {
      if (!t.live) continue;
      const u = (this.nowMs - t.t0) / t.dur;
      if (u >= 1 || u < -0.5) {
        t.live = false;
        continue;
      }
      const k = Math.max(0, u);
      const e = 1 - (1 - k) * (1 - k);
      const s = t.s0 + (t.s1 - t.s0) * e;
      const a = t.a * (1 - k) * (1 - k * 0.3);
      if (t.shape === SHAPE.BEAM) this.sprites.push(SHAPE.BEAM, t.x, t.y, t.z, s, t.h * (0.6 + 0.4 * e), 0, 0, t.r, t.g, t.b, a);
      else this.sprites.push(t.shape, t.x, t.y, t.z, s, s, t.param, 0, t.r, t.g, t.b, a);
    }
  }

  /** Stats for the perf overlay / tests. */
  stats(): { objects: number; pickups: number; shards: number; sprites: number; sparks: number } {
    return { objects: this.defs.length, pickups: this.pickups.active, shards: this.shards.live, sprites: this.sprites.count, sparks: this.sparkPool.liveCount + this.dustPool.liveCount };
  }

  dispose(): void {
    this.clear();
    this.sprites.dispose();
    this.shards.dispose();
    this.pickups.dispose();
    this.sparks?.dispose();
    this.dust?.dispose();
    this.atlas?.dispose();
    this.group.removeFromParent();
  }
}

/** Interpolated looter position from the frame's units (exported for tests). */
export function looterPosition(frame: RenderFrame, units: ReadonlyMap<UnitId, RenderUnit>): Vec2 | null {
  const id = frame.world?.looterId;
  const u = id ? units.get(id) : undefined;
  return u ? u.pos : null;
}
